import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Calendar,
  Building2,
  Clock,
  Award,
  FileText,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    verified: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Verified' },
    active: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Active' },
    expired: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Expired' },
    suspended: { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Suspended' },
    pending: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Pending Review' },
    inactive: { icon: XCircle, color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Inactive' }
  };

  const { icon: Icon, color, bg, label } = config[status] || config.pending;

  return (
    <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${bg}`}>
      <Icon className={`w-4 h-4 mr-2 ${color}`} />
      <span className={`text-sm font-medium ${color}`}>{label}</span>
    </div>
  );
};

// Credential Card
const CredentialCard = ({ credential }) => {
  const isExpired = credential.expiration_date && new Date(credential.expiration_date) < new Date();
  
  return (
    <div className={`p-4 rounded-xl border ${isExpired ? 'border-red-500/30 bg-red-500/5' : 'border-white/10 bg-white/5'}`}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-white font-medium">{credential.name}</h4>
          <p className="text-sm text-slate-400 mt-1">{credential.issuer || 'MedexOVA'}</p>
        </div>
        {isExpired ? (
          <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">Expired</span>
        ) : (
          <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Valid</span>
        )}
      </div>
      
      {credential.expiration_date && (
        <div className="mt-3 flex items-center text-sm text-slate-400">
          <Calendar className="w-4 h-4 mr-2" />
          Expires: {new Date(credential.expiration_date).toLocaleDateString()}
        </div>
      )}
      
      {credential.credential_number && (
        <div className="mt-2 flex items-center text-sm text-slate-400">
          <FileText className="w-4 h-4 mr-2" />
          #{credential.credential_number}
        </div>
      )}
    </div>
  );
};

// Main Verification Page
export const PassportVerification = () => {
  const { passportId } = useParams();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPassport = async () => {
      try {
        const res = await fetch(`${API_URL}/api/verify/passport/${passportId}`);
        if (res.ok) {
          const data = await res.json();
          setPassport(data);
        } else if (res.status === 404) {
          setError('Passport not found');
        } else {
          setError('Failed to verify passport');
        }
      } catch (err) {
        setError('Unable to connect to verification service');
      } finally {
        setLoading(false);
      }
    };

    if (passportId) {
      fetchPassport();
    }
  }, [passportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-neon-cyan animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying passport...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            If you believe this is an error, please contact MedexOVA support.
          </p>
        </div>
      </div>
    );
  }

  const isValid = passport?.status === 'active' || passport?.status === 'verified';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-cyan to-neon-green flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">MedexOVA</h1>
              <p className="text-xs text-slate-400">Credential Verification</p>
            </div>
          </div>
          <a 
            href="https://medexova.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-slate-400 hover:text-white flex items-center"
          >
            Learn more <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Verification Status Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-6 mb-8 ${
            isValid 
              ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30' 
              : 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30'
          }`}
        >
          <div className="flex items-center space-x-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isValid ? 'bg-green-500/30' : 'bg-red-500/30'
            }`}>
              {isValid ? (
                <CheckCircle className="w-8 h-8 text-green-400" />
              ) : (
                <XCircle className="w-8 h-8 text-red-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isValid ? 'Verified Credential Passport' : 'Credential Issue Detected'}
              </h2>
              <p className={`text-sm ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                {isValid 
                  ? 'This credential passport is valid and verified by MedexOVA'
                  : 'This passport may have expired or suspended credentials'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Passport Details */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Driver Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="md:col-span-1"
          >
            <div className="glass-card p-6 text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 flex items-center justify-center mx-auto mb-4 border-2 border-neon-cyan/30">
                <User className="w-12 h-12 text-neon-cyan" />
              </div>
              
              <h3 className="text-xl font-bold text-white">{passport?.driver_name || 'Unknown'}</h3>
              <p className="text-sm text-slate-400 mt-1">{passport?.driver_title || 'Driver'}</p>
              
              <div className="mt-4">
                <StatusBadge status={passport?.status || 'pending'} />
              </div>

              {passport?.company && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-center text-sm text-slate-400">
                    <Building2 className="w-4 h-4 mr-2" />
                    {passport.company}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-white/10 text-xs text-slate-500">
                <p>Passport ID: {passportId?.slice(0, 8)}...</p>
                <p className="mt-1">Verified: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </motion.div>

          {/* Credentials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="md:col-span-2"
          >
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Award className="w-5 h-5 mr-2 text-neon-cyan" />
                  Credentials
                </h3>
                <span className="text-sm text-slate-400">
                  {passport?.credentials?.length || 0} total
                </span>
              </div>

              <div className="space-y-3">
                {passport?.credentials?.length > 0 ? (
                  passport.credentials.map((cred, idx) => (
                    <CredentialCard key={idx} credential={cred} />
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No credentials on file</p>
                  </div>
                )}
              </div>
            </div>

            {/* Verification Info */}
            <div className="glass-card p-6 mt-6">
              <h3 className="text-lg font-bold text-white mb-4">Verification Details</h3>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Issue Date</p>
                  <p className="text-white font-medium">
                    {passport?.created_at 
                      ? new Date(passport.created_at).toLocaleDateString() 
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Last Updated</p>
                  <p className="text-white font-medium">
                    {passport?.updated_at 
                      ? new Date(passport.updated_at).toLocaleDateString() 
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Verification Level</p>
                  <p className="text-white font-medium">{passport?.verification_level || 'Standard'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Issuing Authority</p>
                  <p className="text-white font-medium">MedexOVA</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>This verification was performed by MedexOVA Credential Management System.</p>
          <p className="mt-1">For questions, contact <a href="mailto:support@medexova.com" className="text-neon-cyan hover:underline">support@medexova.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default PassportVerification;
